import axios from "axios";
import { API } from "../App";

/**
 * Iter 100 — Web Push registration + subscribe helpers.
 *
 * Modern browsers only (Chrome / Edge / Firefox / Safari 16+).
 * Silently no-ops on unsupported browsers.
 */

const urlBase64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const isSupported = () =>
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

let _swRegistration = null;

export async function ensureServiceWorker() {
  if (!isSupported()) return null;
  if (_swRegistration) return _swRegistration;
  try {
    _swRegistration = await navigator.serviceWorker.register("/sw.js");
    return _swRegistration;
  } catch (e) {
    return null;
  }
}

export async function ensurePushSubscription(token) {
  if (!isSupported() || !token) return { ok: false, reason: "unsupported_or_no_auth" };
  if (Notification.permission !== "granted") return { ok: false, reason: "permission_not_granted" };

  const reg = await ensureServiceWorker();
  if (!reg) return { ok: false, reason: "sw_register_failed" };

  // Fetch VAPID public key from backend
  let publicKey;
  try {
    const r = await axios.get(`${API}/notifications/vapid-key`, { timeout: 10000 });
    publicKey = r.data?.public_key;
    if (!publicKey) return { ok: false, reason: "no_vapid_key" };
  } catch {
    return { ok: false, reason: "vapid_fetch_failed" };
  }

  let subscription;
  try {
    subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
  } catch (e) {
    return { ok: false, reason: "subscribe_failed", error: String(e) };
  }

  // Send subscription to backend
  try {
    const subJson = subscription.toJSON();
    await axios.post(
      `${API}/notifications/subscribe`,
      { endpoint: subJson.endpoint, keys: subJson.keys },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 },
    );
    return { ok: true, endpoint: subJson.endpoint };
  } catch {
    return { ok: false, reason: "backend_subscribe_failed" };
  }
}

export async function unsubscribePush(token) {
  if (!isSupported() || !token) return false;
  const reg = await ensureServiceWorker();
  if (!reg) return false;
  try {
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return true;
    const subJson = sub.toJSON();
    await axios.delete(`${API}/notifications/subscribe`, {
      headers: { Authorization: `Bearer ${token}` },
      data: { endpoint: subJson.endpoint, keys: subJson.keys },
    });
    await sub.unsubscribe();
    return true;
  } catch {
    return false;
  }
}

export const isPushSupported = isSupported;
